import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useCreateUserMutation, useUpdateUserMutation } from '../../../api/userApi';
import { useLoader } from '../../../hooks/useLoader';
import { useGetCompaniesQuery } from '../../../api/companyApi';
import { useGetBranchesByCompanyIdQuery } from '../../../api/branchApi';
import { useToast } from '../../../hooks/useToast';
import { User } from '../../../interfaces/entities/user';
import { defaultUser } from '../../../defaultValues/defaultUser';
import { EmployeeForm } from './employeeform';
import { uploadImage } from '../../../services/imageUploaderService';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { LoadingIndicator } from '../../shared/alerts/loadingIndicator';
import { getUserImagePath } from '../../../services/storageService';
import { UserRoles } from '../../../enums/userRoles.enum';

export const EmployeeAddContainer: React.FC<{ userRole: UserRoles }> = ({userRole}) => {
    const { companyId, branchId } = useParams<{
        companyId: string;
        branchId: string;
    }>(); // Mark id as optional

    const { user, currentUserIsOwnerOf, isAdmin } = useAuthenticatedUser();

    const [employeeImage, setEmployeeImage] = useState<File | null>(null);
    const navigate = useNavigate();

    const { data: companies = [], isLoading: loadingCompanies } = useGetCompaniesQuery();
    const { data: branches = [], isLoading: loadingBranches } = useGetBranchesByCompanyIdQuery(companyId ?? '');
    const [createUser, createProcess] = useCreateUserMutation();
    const [updateUser, updateProcess] = useUpdateUserMutation();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    // Handle form submission
    const handleFormSubmit = async (values: User) => {
        try {
            showLoader();
            // Step 1: Prepare branch data 
            const employeeData: User = { ...values };

            // Step 2: Save branch data in Firestore and get the document ID
            const createdEmployee = await createUser({ user: employeeData }).unwrap();

            if (createdEmployee) {
                if (employeeImage) {
                    const imageUrl = await uploadImage(getUserImagePath(employeeData.companyId, createdEmployee.id), employeeImage);
                    await updateUser({ id: createdEmployee.id, data: { image: imageUrl } }).unwrap();
                }

                showSuccess("Employee added successfully!");
                navigate(-1); // Navigate back to previous screen
            } else {
                showError("Failed to add Employee!");
            }
        } catch (error: any) {
            console.error('Error while submitting form:', error.message);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };

    const handleImageChange = (file: File | null) => {
        setEmployeeImage(file);
    };

    const companyOptions = companies.map((company) => ({
        id: company.id,
        label: company.name,
    }));

    const branchOptions = branches.map((company) => ({
        id: company.id,
        label: company.name,
    }));

    const isLoading = createProcess.isLoading || updateProcess.isLoading || loadingBranches || loadingCompanies;

    if (isLoading) return <LoadingIndicator message='Loading...' />;
    if (!companyId) return <div>Company Not Found</div>;
    if (!user) return <div>User Not Found</div>;
    if (!currentUserIsOwnerOf(companyId) && !isAdmin) return <div>Unauthorized</div>;

    const initialValues: User = { ...defaultUser(user.id, userRole), companyId, branchId: branchId ?? '' };

    return (
        <Box>
            <EmployeeForm
                title={userRole === UserRoles.employee ? ('Add New Employee') : ('Add Company Owner') }
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
                companyOptions={companyOptions}
                branchOptions={branchOptions}
                onImageChange={handleImageChange}
                isSubmitting={isLoading}
                onCancel={() => navigate(-1)}
            />
        </Box>
    );
};
